import {
  Box,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";

// モック（commercial-registry-csv-excel-mock.html）の配色と寸法。①〜③で同じ値を使う
export const mock = {
  teal: "#25849b",
  tealDark: "#1e6f83",
  tealSoft: "#eaf5f7",
  ink: "#252a2d",
  muted: "#737b80",
  line: "#e2e5e7",
} as const;

/** モックの .btn。高さ38px・角丸3px・太字。主ボタンは最小幅220px、枠線ボタンはteal */
export const buttonSx = {
  base: {
    height: 38,
    borderRadius: "3px",
    px: "22px",
    fontWeight: 700,
    whiteSpace: "nowrap",
    // MUIの既定の大文字化で「Excel」が「EXCEL」にならないようにする
    textTransform: "none",
  },
  primary: {
    minWidth: { xs: 0, [MOBILE_BREAKPOINT]: 220 },
    bgcolor: mock.teal,
    boxShadow: "0 1px 3px rgba(26,95,110,.25)",
    "&:hover": { bgcolor: mock.tealDark },
  },
  secondary: {
    color: "#485055",
    borderColor: "#cfd5d7",
    "&:hover": { bgcolor: "#f6f8f9", borderColor: "#cfd5d7" },
  },
  outline: { color: mock.tealDark, borderColor: mock.teal },
  small: { height: 31, px: "12px", fontSize: 12 },
  link: { color: mock.tealDark, fontSize: 12, textTransform: "none" },
} satisfies Record<string, SxProps<Theme>>;

// モックの .panel-body（30px 32px 34px）。SPは横幅が狭いため詰める
const bodyDefaultSx: SxProps<Theme> = {
  px: { xs: 2, [MOBILE_BREAKPOINT]: "32px" },
  pt: { xs: "20px", [MOBILE_BREAKPOINT]: "30px" },
  pb: { xs: "24px", [MOBILE_BREAKPOINT]: "34px" },
};

interface Props {
  title: string;
  note?: React.ReactNode;
  /** ヘッダーの右側に置く操作や補足 */
  actions?: React.ReactNode;
  /** 本文の余白。表を広く見せたい③だけ狭める（指定すると既定の余白を置き換える） */
  bodySx?: SxProps<Theme>;
  /** パネルの下端に帯で置く要素（③の取得対象件数と送信ボタン） */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * モックの .panel / .panel-head / .panel-body。
 * 共通の SectionPaper は内側の余白を持たず、見出しの区切り線もないため、この画面専用に用意する
 */
export const Panel = ({
  title,
  note,
  actions,
  bodySx,
  footer,
  children,
}: Props): JSX.Element => (
  <Box
    component="section"
    sx={{
      border: "1px solid #d9dddf",
      borderRadius: "3px",
      boxShadow: "0 2px 7px rgba(22, 38, 45, .13)",
      bgcolor: "#fff",
      overflow: "hidden",
      color: mock.ink,
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap="12px 20px"
      sx={{
        minHeight: 63,
        px: { xs: 2, [MOBILE_BREAKPOINT]: "22px" },
        py: 2,
        borderBottom: `1px solid ${mock.line}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700 }}>
          {title}
        </Typography>
        {note && (
          <Typography sx={{ color: mock.muted, fontSize: 12, mt: "5px" }}>
            {note}
          </Typography>
        )}
      </Box>
      {actions}
    </Stack>
    <Box sx={bodySx ?? bodyDefaultSx}>{children}</Box>
    {footer && (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        sx={{
          px: { xs: 2, [MOBILE_BREAKPOINT]: "24px" },
          py: "20px",
          borderTop: `1px solid ${mock.line}`,
          bgcolor: "#fcfdfd",
        }}
      >
        {footer}
      </Stack>
    )}
  </Box>
);

/** モックの .actions。ボタンを中央に寄せて並べる */
export const PanelActions = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => (
  <Stack
    direction="row"
    justifyContent="center"
    flexWrap="wrap"
    gap="12px"
    sx={{ pt: "26px", pb: "3px" }}
  >
    {children}
  </Stack>
);
